import OpponentAi from '../../ai/opponentAi'
import { UnitAction } from '../../engine/actions/action'
import Hex from '../../engine/hex'
import { ICell } from '../../engine/map'
import Unit, { UnitStatus } from '../../engine/unit'
import BaseStore from '../utils/store'

import { IState } from './index'

const TURN_BANNER_DURATION_MS = 500

export default class Store extends BaseStore<IState> {
  turnBannerTimeout?: number
  debugLog: string[] = []
  debugTurnKey?: string

  addDebugLog(message: string) {
    this.addDebugLogs([message])
  }

  syncVisibleDebugLog() {
    if (this.state.showDebugConsole) {
      this.set({ debugLog: this.debugLog })
    }
  }

  currentDebugTurnLabel() {
    const { game, playerFaction } = this.state
    const turnName = game.currenFaction.id === playerFaction
      ? 'Player'
      : 'Enemy'
    return `${turnName} Turn ${game.epoch + 1}`
  }

  addDebugTurnHeader() {
    const turnLabel = this.currentDebugTurnLabel()
    if (this.debugTurnKey === turnLabel) {
      return
    }

    this.debugTurnKey = turnLabel
    this.debugLog = [...this.debugLog, turnLabel].slice(-200)
    this.syncVisibleDebugLog()
  }

  addDebugLogs(messages: string[]) {
    if (messages.length === 0) {
      return
    }

    const nextMessages = messages.slice()
    if (this.debugTurnKey !== this.currentDebugTurnLabel()) {
      this.addDebugTurnHeader()
    }

    this.debugLog = [...this.debugLog, ...nextMessages].slice(-200)
    this.syncVisibleDebugLog()
  }

  completeUnitWithoutAction(unit: Unit, reason: string) {
    if (!unit.actionPerformed) {
      this.addDebugLog(
        `${this.unitDebugName(unit)} is marked complete (${reason})`,
      )
    }
    unit.actionPerformed = true
  }

  debugUnits() {
    const units: Unit[] = []
    this.state.game.things.forEach(thing => {
      if (thing instanceof Unit) {
        units.push(thing)
      }
    })
    return units
  }

  unitDebugName(unit: Unit) {
    const sameTypeUnits = this.debugUnits()
      .filter(u => u.type.name === unit.type.name)
    const idx = sameTypeUnits.indexOf(unit)
    const number = idx >= 0 ? idx + 1 : 1
    return `${unit.type.name} ${number}`
  }

  unitDebugNameById(unitId: string) {
    const unit = this.state.game.things.get(unitId)
    return unit instanceof Unit
      ? this.unitDebugName(unit)
      : 'Unknown unit'
  }

  tileDebugName(pos: Hex) {
    return `tile ${pos.toString()}`
  }

  actionTargetDebugName(target: Hex) {
    const thing = this.state.game.map.cellAt(target).thing
    return thing instanceof Unit
      ? this.unitDebugName(thing)
      : this.tileDebugName(target)
  }

  statusDebugName(status: UnitStatus) {
    switch (status) {
      case UnitStatus.Sleeping:
        return 'asleep'
      case UnitStatus.Burning:
        return 'burning'
      case UnitStatus.Slowed:
        return 'slowed'
      case UnitStatus.Guard:
        return 'guarding'
      default:
        return 'affected'
    }
  }

  durationDebugName(exp: number) {
    if (exp <= 0) {
      return 'this turn'
    }

    return `${exp} ${exp === 1 ? 'turn' : 'turns'}`
  }

  onDebugMove = async ({ unit, path }) => {
    const destination = path[path.length - 1]
    this.addDebugLog(
      `${this.unitDebugName(unit)} moves to ${this.tileDebugName(destination)}`,
    )
  }

  onDebugAction = async ({ action, target, result }) => {
    const messages = [
      `${this.unitDebugName(action.unit)} uses ${action.name} on ` +
      this.actionTargetDebugName(target),
    ]

    result.targets.forEach(effect => {
      const targetName = this.unitDebugNameById(effect.unitId)
      const damage = effect.damage || 0
      if (damage > 0) {
        messages.push(`${targetName} takes ${damage} damage`)
      } else if (damage < 0) {
        messages.push(`${targetName} heals ${-damage} hp`)
      }
      if (effect.status) {
        messages.push(
          `${targetName} is ${this.statusDebugName(effect.status.status)} ` +
          `for ${this.durationDebugName(effect.status.exp)}`,
        )
      }
      if (effect.newPosition) {
        messages.push(
          `${targetName} moves to ${this.tileDebugName(effect.newPosition)}`,
        )
      }
    })

    this.addDebugLogs(messages)
  }

  logSelection(previousUnit: Unit | undefined, selection) {
    const selectedUnit = selection && selection.unit && selection.unit.unit
    if (selectedUnit && selectedUnit !== previousUnit) {
      this.addDebugLog(`User selected ${this.unitDebugName(selectedUnit)}`)
    }
  }

  indexCells(hexes: Hex[]): {[idx: string]: Hex} {
    const index = {}
    hexes.forEach(h => index[h.toString()] = h)
    return index
  }

  isEnemy(unit: Unit) {
    return unit.factionId !== this.state.playerFaction
  }

  isThreateningAction(action: UnitAction) {
    const damage = action.params && action.params.damage
    return action.isAvailable
      && (damage > 0 || action.name === 'Sleep')
  }

  actionThreatExtension(action: UnitAction) {
    if (action.name === 'Rolling Stone') {
      return 3
    }

    return action.params && action.params.area
      ? action.params.area
      : 0
  }

  isDamagingAction(action: UnitAction) {
    const damage = action.params && action.params.damage
    return action.canExecute && damage > 0
  }

  actionHitsEnemy(action: UnitAction, target: Hex) {
    return action.performAction(target).targets.some(effect => {
      const unit = this.state.game.things.get(effect.unitId)
      return unit instanceof Unit
        && this.isEnemy(unit)
        && (effect.damage || 0) > 0
    })
  }

  usefulEnemyActionTargets(action: UnitAction) {
    return action.targets().filter(target =>
      this.actionHasUsefulTarget(action, target),
    )
  }

  canUseActionOnEnemy(action: UnitAction) {
    return this.usefulEnemyActionTargets(action).length > 0
  }

  shouldExecutePlayerAction(action: UnitAction, target: Hex) {
    return this.isMovementAction(action)
      || this.actionHasUsefulTarget(action, target)
  }

  statusEffectIsUseful(unit: Unit, status: UnitStatus) {
    if (status === UnitStatus.Guard) {
      return false
    }

    const expiration = unit.status.get(status)
    return expiration === undefined || expiration <= 0
  }

  actionHasUsefulTarget(action: UnitAction, target: Hex) {
    return action.performAction(target).targets.some(effect => {
      const unit = this.state.game.things.get(effect.unitId)
      if (!(unit instanceof Unit)) {
        return false
      }

      const friendly = unit.factionId === this.state.playerFaction
      const damage = effect.damage || 0
      if (damage > 0) {
        return !friendly
      }
      if (damage < 0) {
        return friendly && unit.hp < unit.type.hp
      }
      if (effect.status) {
        return !friendly
          && this.statusEffectIsUseful(unit, effect.status.status)
      }

      return false
    })
  }

  hasUsefulActionTarget(unit: Unit) {
    return unit.actions
      .filter(action => action.canExecute)
      .some(action =>
        action.targets().some(target =>
          this.actionHasUsefulTarget(action, target),
        ),
      )
  }

  canMoveIntoAny(unit: Unit, targets: {[idx: string]: Hex}) {
    return unit.moveTargets().some(target => !!targets[target.toString()])
  }

  shouldWaitForDeferredSupport(unit: Unit) {
    const heal = unit.actions.find(action =>
      action.name === 'Heal' && action.canExecute,
    )
    if (!heal) {
      return false
    }

    const healTargets = this.indexCells(heal.targets())
    return this.state.game.factionUnits[this.state.playerFaction]
      .some(ally =>
        ally !== unit
        && ally.hp < ally.type.hp
        && ally.canPerformAction
        && this.canMoveIntoAny(ally, healTargets),
      )
  }

  shouldCompleteAfterMovement(unit: Unit) {
    return unit.factionId === this.state.playerFaction
      && (unit.mp <= 0 || !this.hasWalkMove(unit))
      && unit.canPerformAction
      && !this.hasUsefulActionTarget(unit)
  }

  getAutoGuardAction(unit: Unit) {
    return unit.actions.find(action =>
      action.name === 'Guard' && action.canExecute,
    )
  }

  isMovementAction(action: UnitAction) {
    return action.name === 'Jump' || action.name === 'Teleport'
  }

  hasUsefulMovementAction(unit: Unit) {
    return unit.actions
      .filter(action => action.canExecute && this.isMovementAction(action))
      .some(action => action.targets().length > 0)
  }

  shouldOfferMovementActionAfterMovement(unit: Unit) {
    return this.shouldCompleteAfterMovement(unit)
      && this.hasUsefulMovementAction(unit)
  }

  shouldKeepUnitActiveAfterMovement(unit: Unit) {
    return this.hasUsefulActionTarget(unit)
      || this.hasUsefulMovementAction(unit)
  }

  hasWalkMove(unit: Unit) {
    return unit.moveTargets().length > 0
  }

  shouldAutoCompleteIfNoOtherActions(unit: Unit) {
    return unit.factionId === this.state.playerFaction
      && unit.canPerformAction
      && !this.hasWalkMove(unit)
      && !this.hasUsefulActionTarget(unit)
      && !this.shouldWaitForDeferredSupport(unit)
  }

  shouldAutoCompleteSelectedUnit(unit: Unit) {
    return this.shouldAutoCompleteIfNoOtherActions(unit)
      && !this.hasUsefulMovementAction(unit)
      && !this.hasNonPendingPlayerActions(unit)
  }

  hasNonPendingPlayerActions(ignoredUnit?: Unit) {
    return this.state.game.factionUnits[this.state.playerFaction]
      .some(unit =>
        unit !== ignoredUnit
        && unit.canPerformAction
        && !this.shouldAutoCompleteIfNoOtherActions(unit),
      )
  }

  completeAutoCompletableUnitsIfReady(primary?: Unit) {
    if (this.hasNonPendingPlayerActions(primary)) {
      return
    }

    if (primary && this.shouldAutoCompleteIfNoOtherActions(primary)) {
      this.completeUnitWithoutAction(primary, 'no useful action')
    }

    this.state.game.factionUnits[this.state.playerFaction]
      .filter(unit => this.shouldAutoCompleteIfNoOtherActions(unit))
      .forEach(unit =>
        this.completeUnitWithoutAction(unit, 'no useful action'),
      )
  }

  finishPendingMovementSelection = async (primary: Unit, cell: ICell) => {
    if (!this.hasNonPendingPlayerActions(primary)) {
      this.completeAutoCompletableUnitsIfReady(primary)

      if (this.state.game.checkGameOver()) {
        this.set({ selection: undefined, hover: undefined })
        return
      }

      if (this.shouldAutoEndPlayerTurn()) {
        await this.endTurn()
        return
      }
    }

    const nextSelection = this.getCellInfo(cell)
    this.logSelection(primary, nextSelection)
    this.set({ selection: nextSelection, hover: undefined })
  }

  shouldDismissPendingMovementSelection(unit, action, cell: ICell) {
    if (!unit || !unit.pendingMovementCompletion) {
      return false
    }

    if (unit.unit.factionId !== this.state.playerFaction) {
      return false
    }

    if (cell.pos.equals(unit.unit.pos)) {
      return false
    }

    return !action || !action.targets[cell.pos.toString()]
  }

  attacksThatCanHitEnemy(unit: Unit) {
    return unit.actions.filter(action =>
      this.isDamagingAction(action) && this.canUseActionOnEnemy(action),
    )
  }

  actionTargetsCell(action: UnitAction, target: Hex) {
    return action.targets().some(t => t.toString() === target.toString())
  }

  actionTargetPriority(action: UnitAction, target: Hex) {
    const distance = target.distance(action.unit.pos)
    const range = action.params && action.params.range
    const maxRange = range ? range[0] : distance
    const minRange = range ? range[1] : distance
    return distance * 100 + maxRange + minRange
  }

  directActionForTarget(unit: Unit, target: Hex) {
    const targetUnit = this.state.game.map.cellAt(target).thing
    if (!(targetUnit instanceof Unit) || !this.isEnemy(targetUnit)) {
      return
    }

    const actions = unit.actions.filter(action =>
      action.canExecute
      && !this.isMovementAction(action)
      && action.name !== 'Guard'
      && this.actionTargetsCell(action, target)
      && this.actionHasUsefulTarget(action, target),
    )
      .sort((a, b) =>
        this.actionTargetPriority(a, target)
          - this.actionTargetPriority(b, target),
      )

    if (actions.length === 0) {
      return
    }

    if (unit.type.name === 'Cleric') {
      const sleep = actions.find(action => action.name === 'Sleep')
      if (sleep) {
        return sleep
      }
    }

    if (unit.type.name === 'Barbarian') {
      const twoHanded = actions.find(action =>
        action.name === 'Two Handed Sword Attack',
      )
      if (twoHanded && this.enemyDamageCount(twoHanded, target) > 1) {
        return twoHanded
      }

      const melee = actions.find(action => action.name === 'Melee Attack')
      if (melee) {
        return melee
      }
    }

    const damaging = actions.find(action => this.isDamagingAction(action))
    return damaging || actions[0]
  }

  moveAndActionForTarget(
    unit: Unit,
    target: Hex,
    paths: { [idx: string]: Hex },
  ) {
    const targetUnit = this.state.game.map.cellAt(target).thing
    if (!(targetUnit instanceof Unit) || !this.isEnemy(targetUnit)) {
      return
    }

    let best
    const landings = this.uniqueHexes(Object.keys(paths).map(idx => paths[idx]))
    landings.forEach(landing => {
      const moveDistance = unit.pos.distance(landing)
      this.withUnitAt(unit, landing, () => {
        const action = this.directActionForTarget(unit, target)
        if (!action) {
          return
        }

        const distance = landing.distance(target)
        if (
          !best
          || distance < best.distance
          || (distance === best.distance && moveDistance < best.moveDistance)
        ) {
          best = { action, moveTo: landing, distance, moveDistance }
        }
      })
    })

    return best
  }

  tryActionOrMoveForTarget = async (
    unit: Unit,
    target: Hex,
    paths: { [idx: string]: Hex },
  ) => {
    const directAction = this.directActionForTarget(unit, target)
    if (directAction) {
      await directAction.execute(target)
      await this.finishPlayerAction()
      return true
    }

    const moveAction = this.moveAndActionForTarget(unit, target, paths)
    if (!moveAction) {
      return false
    }

    await unit.move(moveAction.moveTo)
    await moveAction.action.execute(target)
    await this.finishPlayerAction()
    return true
  }

  enemyDamageCount(action: UnitAction, target: Hex) {
    return action.performAction(target).targets.filter(effect => {
      const unit = this.state.game.things.get(effect.unitId)
      return unit instanceof Unit
        && this.isEnemy(unit)
        && (effect.damage || 0) > 0
    }).length
  }

  usefulEnemyDamageTargets(action: UnitAction) {
    return action.targets().filter(target =>
      this.enemyDamageCount(action, target) > 0,
    )
  }

  usefulTargetsForAction(action: UnitAction) {
    if (this.isMovementAction(action)) {
      if (this.hasWalkMove(action.unit)) {
        return []
      }
      return action.targets()
    }

    if (action.name === 'Guard') {
      return []
    }

    return this.usefulEnemyActionTargets(action)
  }

  setSelectedAction(selection, action: UnitAction, usefulTargets?: Hex[]) {
    const selectedTargets = usefulTargets || this.usefulEnemyActionTargets(action)
    selection.unit.action = {
      action,
      targets: this.indexCells(action.targets()),
      selectedTarget: selectedTargets.length === 1
        ? selectedTargets[0]
        : undefined,
    }
    return selection
  }

  autoSelectOnlyUsefulAction(selection) {
    const unit = selection && selection.unit && selection.unit.unit
    if (!unit || unit.factionId !== this.state.playerFaction) {
      return selection
    }

    if (selection.unit.action) {
      return selection
    }

    const actions = unit.actions
      .filter(action => action.canExecute)
      .map(action => ({
        action,
        usefulTargets: this.usefulTargetsForAction(action),
      }))
      .filter(actionInfo => actionInfo.usefulTargets.length > 0)

    if (actions.length === 1) {
      return this.setSelectedAction(
        selection,
        actions[0].action,
        actions[0].usefulTargets,
      )
    }

    return selection
  }

  autoSelectBarbarianAttack(selection, unit: Unit) {
    if (unit.type.name !== 'Barbarian') {
      return selection
    }

    const twoHanded = unit.actions.find(action =>
      action.name === 'Two Handed Sword Attack'
      && this.isDamagingAction(action)
      && this.canUseActionOnEnemy(action),
    )
    if (twoHanded) {
      const multiTargets = twoHanded.targets().filter(target =>
        this.enemyDamageCount(twoHanded, target) > 1,
      )
      if (multiTargets.length > 0) {
        return this.setSelectedAction(selection, twoHanded, multiTargets)
      }
    }

    const melee = unit.actions.find(action =>
      action.name === 'Melee Attack'
      && this.isDamagingAction(action)
      && this.canUseActionOnEnemy(action),
    )
    if (melee) {
      return this.setSelectedAction(
        selection,
        melee,
        this.usefulEnemyDamageTargets(melee),
      )
    }

    return selection
  }

  autoSelectPredictedAction(selection, afterMove = false) {
    const unit = selection && selection.unit && selection.unit.unit
    if (!unit || unit.factionId !== this.state.playerFaction) {
      return selection
    }

    if (!afterMove && this.hasWalkMove(unit)) {
      return selection
    }

    selection = this.autoSelectBarbarianAttack(selection, unit)
    if (selection.unit.action) {
      return selection
    }

    const attacks = this.attacksThatCanHitEnemy(unit)
    if (attacks.length === 1) {
      const usefulTargets = this.usefulEnemyActionTargets(attacks[0])
      selection = this.setSelectedAction(selection, attacks[0], usefulTargets)
    } else {
      selection = this.autoSelectOnlyUsefulAction(selection)
    }

    return selection
  }

  longestThreatRange(unit: Unit) {
    return unit.actions
      .filter(action => this.isThreateningAction(action))
      .reduce((max, action) => {
        const extension = this.actionThreatExtension(action)
        return action.targets().reduce((actionMax, target) => {
          return Math.max(actionMax, target.distance(unit.pos) + extension)
        }, max)
      }, 0)
  }

  enemyThreats(unit: Unit) {
    const range = this.longestThreatRange(unit)
    if (range <= 0) {
      return []
    }

    const positions = [unit.pos, ...unit.moveTargets(true, unit.type.mp)]
    const threats: Hex[] = []
    positions.forEach(pos => {
      pos.range(range)
        .filter(this.state.game.map.isIn)
        .forEach(threat => threats.push(threat))
    })

    return threats
  }

  uniqueHexes(hexes: Hex[]) {
    const seen = new Set<string>()
    const unique: Hex[] = []
    hexes.forEach(hex => {
      const id = hex.toString()
      if (!seen.has(id)) {
        seen.add(id)
        unique.push(hex)
      }
    })
    return unique
  }

  withUnitAt<T>(unit: Unit, pos: Hex, fn: () => T): T {
    const from = unit.pos
    if (from.equals(pos)) {
      return fn()
    }

    const map = this.state.game.map
    const fromCell = map.cellAt(from)
    const toCell = map.cellAt(pos)
    const fromThing = fromCell.thing
    const toThing = toCell.thing

    if (fromThing === unit) {
      delete fromCell.thing
    }
    unit.pos = pos
    toCell.thing = unit

    try {
      return fn()
    } finally {
      if (toThing) {
        toCell.thing = toThing
      } else {
        delete toCell.thing
      }
      unit.pos = from
      if (fromThing) {
        fromCell.thing = fromThing
      } else {
        delete fromCell.thing
      }
    }
  }

  canUnitStandOn(unit: Unit, pos: Hex) {
    const cell = this.state.game.map.cellAt(pos)
    const thing = cell.thing
    if (thing && thing !== unit) {
      return false
    }

    if (thing === unit) {
      delete cell.thing
    }
    const canStand = unit.canWalkOn(cell)
    if (thing) {
      cell.thing = thing
    }

    return canStand
  }

  enemyCanActNextTurn(enemy: Unit) {
    const sleep = enemy.status.get(UnitStatus.Sleeping)
    return sleep === undefined || sleep <= 0
  }

  enemyNextTurnMovement(enemy: Unit) {
    let movement = enemy.type.mp
    if (enemy.status.has(UnitStatus.Slowed)) {
      movement--
    }
    return Math.max(0, movement)
  }

  actionThreatensUnit(action: UnitAction, unit: Unit, target: Hex) {
    return action.performAction(target).targets.some(effect =>
      effect.unitId === unit.id
      && (
        (effect.damage || 0) > 0
        || (!!effect.status && effect.status.status !== UnitStatus.Guard)
      ),
    )
  }

  enemyCanThreatenUnitAt(enemy: Unit, unit: Unit, pos: Hex) {
    if (!this.enemyCanActNextTurn(enemy)) {
      return false
    }

    return this.withUnitAt(unit, pos, () => {
      const landings = this.uniqueHexes([
        enemy.pos,
        ...enemy.moveTargets(true, this.enemyNextTurnMovement(enemy)),
      ])

      return landings.some(landing =>
        this.withUnitAt(enemy, landing, () =>
          enemy.actions
            .filter(action => this.isThreateningAction(action))
            .some(action =>
              action.targets().some(target =>
                this.actionThreatensUnit(action, unit, target),
              ),
            ),
        ),
      )
    })
  }

  movementThreatInfoForPlayer(unit: Unit, paths: { [idx: string]: Hex }) {
    const possiblePositions = this.uniqueHexes([
      unit.pos,
      ...Object.keys(paths).map(idx => paths[idx]),
    ])
    const factionUnits = this.state.game.factionUnits
    const threats: Hex[] = []
    const dangerPaths: Hex[] = []
    const attackers: Unit[] = []
    const attackersByPath: { [idx: string]: Unit[] } = {}
    const threatIds = new Set<string>()
    const dangerPathIds = new Set<string>()
    const threatResults: { [idx: string]: boolean } = {}
    const canThreaten = (enemy: Unit, pos: Hex) => {
      const idx = `${enemy.id}:${pos.toString()}`
      if (threatResults[idx] === undefined) {
        threatResults[idx] = this.enemyCanThreatenUnitAt(enemy, unit, pos)
      }
      return threatResults[idx]
    }

    Object.keys(factionUnits)
      .filter(factionId => factionId !== this.state.playerFaction)
      .forEach(factionId => factionUnits[factionId].forEach(enemy => {
        const threatenedMovePositions = possiblePositions.filter(pos =>
          canThreaten(enemy, pos),
        )

        if (threatenedMovePositions.length > 0) {
          const enemyThreats = this.uniqueHexes(this.enemyThreats(enemy))
            .filter(pos => this.canUnitStandOn(unit, pos))
            .filter(pos => canThreaten(enemy, pos))
          attackers.push(enemy)
          enemyThreats.forEach(threat => {
            const idx = threat.toString()
            if (!threatIds.has(idx)) {
              threatIds.add(idx)
              threats.push(threat)
            }
          })
          threatenedMovePositions.forEach(threat => {
            const idx = threat.toString()
            attackersByPath[idx] = attackersByPath[idx] || []
            attackersByPath[idx].push(enemy)
            if (!dangerPathIds.has(idx)) {
              dangerPathIds.add(idx)
              dangerPaths.push(threat)
            }
          })
        }
      }))

    return { threats, dangerPaths, attackers, attackersByPath }
  }

  indexUnits(units: Unit[]): { [idx: string]: Unit } {
    const index = {}
    units.forEach(unit => index[unit.id] = unit)
    return index
  }

  indexAttackersByPath(
    attackersByPath: { [idx: string]: Unit[] },
  ): { [idx: string]: { [idx: string]: Unit } } {
    const index = {}
    Object.keys(attackersByPath).forEach(path =>
      index[path] = this.indexUnits(attackersByPath[path]),
    )
    return index
  }

  isHarmfulFriendlyEffect(effect) {
    const unit = this.state.game.things.get(effect.unitId)
    if (!(unit instanceof Unit) || unit.factionId !== this.state.playerFaction) {
      return false
    }

    return (effect.damage || 0) > 0
      || (!!effect.status && effect.status.status !== UnitStatus.Guard)
  }

  wouldHarmFriendlyUnit(action: UnitAction, target: Hex) {
    return action.performAction(target).targets
      .some(effect => this.isHarmfulFriendlyEffect(effect))
  }

  getCellInfo(target: ICell) {
    const { thing } = target
    const isEnemy = thing instanceof Unit
      && this.isEnemy(thing)

    return {
      cell: target,
      unit: (thing && thing instanceof Unit)
        ? this.getUnitInfo(thing, isEnemy)
        : undefined,
    }
  }

  getUnitInfo(unit: Unit, isEnemy: boolean) {
    const paths = this.indexCells(
      unit.moveTargets(isEnemy, isEnemy ? unit.type.mp : unit.mp),
    )
    const playerThreatInfo = isEnemy
      ? {
        threats: [] as Hex[],
        dangerPaths: [] as Hex[],
        attackers: [] as Unit[],
        attackersByPath: {} as { [idx: string]: Unit[] },
      }
      : this.movementThreatInfoForPlayer(unit, paths)
    const threats = isEnemy
      ? this.enemyThreats(unit)
      : playerThreatInfo.threats

    return {
      unit,
      paths,
      threats: this.indexCells(threats),
      dangerPaths: isEnemy
        ? undefined
        : this.indexCells(playerThreatInfo.dangerPaths),
      attackers: isEnemy
        ? undefined
        : this.indexUnits(playerThreatInfo.attackers),
      attackersByPath: isEnemy
        ? undefined
        : this.indexAttackersByPath(playerThreatInfo.attackersByPath),
    }
  }

  shouldAutoEndPlayerTurn() {
    const { game, playerFaction } = this.state
    return game.currenFaction.id === playerFaction
      && game.factionUnits[playerFaction].every(u => !u.canPerformAction)
  }

  finishPlayerAction = async () => {
    if (this.state.game.checkGameOver()) {
      this.set({ selection: undefined, hover: undefined })
      return
    }

    this.completeAutoCompletableUnitsIfReady()
    this.set({ selection: undefined, hover: undefined })

    if (this.shouldAutoEndPlayerTurn()) {
      await this.endTurn()
    }
  }

  showTurnBanner() {
    const { game, playerFaction } = this.state
    const faction = game.currenFaction
    const isPlayer = faction.id === playerFaction
    const key = Date.now()

    if (this.turnBannerTimeout !== undefined) {
      window.clearTimeout(this.turnBannerTimeout)
      this.turnBannerTimeout = undefined
    }

    this.set({
      turnBanner: {
        key,
        text: isPlayer ? 'Player Turn' : 'Enemy Turn',
        isPlayer,
      },
    })

    return new Promise<void>(resolve => {
      this.turnBannerTimeout = window.setTimeout(() => {
        const banner = this.state.turnBanner
        if (banner && banner.key === key) {
          this.set({ turnBanner: undefined })
        }
        this.turnBannerTimeout = undefined
        resolve()
      }, TURN_BANNER_DURATION_MS)
    })
  }

  selectCell = async (cell: ICell) => {
    if (this.state.turnBanner) {
      return
    }

    await this.state.game.emit('animation:skip', {})

    const { selection, playerFaction } = this.state
    const unit = selection && selection.unit
    const action = unit && unit.action

    let newSelection
    if (this.shouldDismissPendingMovementSelection(unit, action, cell)) {
      await this.finishPendingMovementSelection(unit!.unit, cell)
      return
    } else if (action && action.targets[cell.pos.toString()]) {
      if (unit!.unit.factionId === playerFaction) {
        if (!this.shouldExecutePlayerAction(action.action, cell.pos)) {
          if (unit!.pendingMovementCompletion) {
            await this.finishPendingMovementSelection(unit!.unit, cell)
            return
          }
          newSelection = this.getCellInfo(cell)
        } else {
          await action.action.execute(cell.pos)
          await this.finishPlayerAction()
          return
        }
      } else {
        newSelection = selection
      }
    } else if (
      !action && unit && unit.unit.factionId === playerFaction
    ) {
      if (await this.tryActionOrMoveForTarget(
        unit.unit,
        cell.pos,
        unit.paths,
      )) {
        return
      }

      if (unit.paths[cell.pos.toString()]) {
        await unit.unit.move(cell.pos)
        newSelection = this.getCellInfo(cell)
        if (this.shouldCompleteAfterMovement(unit.unit)) {
          if (this.shouldOfferMovementActionAfterMovement(unit.unit)) {
            newSelection.unit!.pendingMovementCompletion = true
            newSelection = this.autoSelectPredictedAction(newSelection, true)
            this.set({ selection: newSelection, hover: undefined })
            return
          }
          if (this.shouldKeepUnitActiveAfterMovement(unit.unit)) {
            this.set({ selection: newSelection, hover: undefined })
            return
          }
          const guard = this.getAutoGuardAction(unit.unit)
          if (guard) {
            await guard.execute(unit.unit.pos)
          } else if (this.shouldWaitForDeferredSupport(unit.unit)) {
            this.set({ selection: newSelection, hover: undefined })
            return
          } else {
            this.completeUnitWithoutAction(
              unit.unit,
              'no useful action after moving',
            )
          }
          await this.finishPlayerAction()
          return
        }
        newSelection = this.autoSelectPredictedAction(newSelection, true)
      } else {
        newSelection = this.getCellInfo(cell)
      }
    } else if (
      action && unit && unit.unit.factionId === playerFaction
    ) {
      if (
        !this.isMovementAction(action.action)
        && await this.tryActionOrMoveForTarget(
          unit.unit,
          cell.pos,
          unit.paths,
        )
      ) {
        return
      }

      newSelection = this.getCellInfo(cell)
    } else {
      newSelection = this.getCellInfo(cell)
      if (
        unit && unit.pendingMovementCompletion
        && newSelection.unit
        && cell.pos.equals(unit.unit.pos)
      ) {
        newSelection.unit.pendingMovementCompletion = true
      }
      newSelection = this.autoSelectPredictedAction(newSelection)
    }

    const selectedUnit = newSelection && newSelection.unit
      && newSelection.unit.unit
    if (selectedUnit && this.shouldAutoCompleteSelectedUnit(selectedUnit)) {
      this.completeUnitWithoutAction(selectedUnit, 'no useful action')
      await this.finishPlayerAction()
      return
    }

    this.logSelection(unit && unit.unit, newSelection)
    this.set({ selection: newSelection, hover: undefined })
  }

  selectAction = async (action: UnitAction) => {
    if (this.state.turnBanner) {
      return
    }

    const { selection } = this.state

    if (action.name === 'Guard' && action.canExecute) {
      await action.execute(action.unit.pos)
      await this.finishPlayerAction()
      return
    }

    selection!.unit!.action = {
      action,
      targets: this.indexCells(action.targets()),
    }

    this.set({ selection, hover: undefined })
  }

  hover = (cell: ICell | null) => {
    if (this.state.turnBanner) {
      if (!cell) {
        this.set({ hover: undefined })
      }
      return
    }

    if (!cell) {
      this.set({ hover: undefined })
      return
    }

    const { selection } = this.state
    const unit = selection && selection.unit
    const action = unit && unit.action
    let nextSelection = selection

    if (action) {
      const selectedTarget = action.targets[cell.pos.toString()]
      nextSelection = {
        cell: selection!.cell,
        unit: {
          unit: unit!.unit,
          paths: unit!.paths,
          threats: unit!.threats,
          dangerPaths: unit!.dangerPaths,
          attackers: unit!.attackers,
          attackersByPath: unit!.attackersByPath,
          pendingMovementCompletion: unit!.pendingMovementCompletion,
          action: {
            action: action.action,
            targets: action.targets,
            selectedTarget,
            area: action.action.params.area && selectedTarget
              ? this.indexCells(cell.pos.range(action.action.params.area))
              : undefined,
          },
        },
      }
    }

    this.set({ selection: nextSelection, hover: this.getCellInfo(cell) })
  }

  endTurn = async (allowEnemyTurn = false) => {
    if (this.state.turnBanner) {
      return
    }

    const { game, playerFaction } = this.state
    if (!allowEnemyTurn && game.currenFaction.id !== playerFaction) {
      return
    }

    await game.endTurn()
    this.addDebugTurnHeader()
    this.set({
      selection: undefined,
      hover: undefined,
    })
    await this.showTurnBanner()

    const currentFactionId = game.currenFaction.id
    if (currentFactionId !== playerFaction) {
      const opponent = new OpponentAi(this)
      await opponent.performTurn()
    }
    this.set({})
  }
}



// WEBPACK FOOTER //
// ./src/ui/stageView/store.ts